/**
 * ASC 340-40 Phase 12d — Contract Cost Panel.
 *
 * Parent container for Phase 12 (ASC 340-40) reviewer UI. Lives inside
 * ProgressTracker as its 5th tab when the contract has ≥1 cost asset.
 *
 * 3 sub-sections:
 *   - Capitalized Cost Assets (12a) — list assets + create modal
 *   - Amortization Events (12b) — per-asset audit browse
 *   - Impairment Testing (12c) — manual test + history + cancel/consume
 */
import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount } from '../../constants';
import DataTable, { Column } from './DataTable';
import Modal from './Modal';
import TabBar from './TabBar';
import ImpairmentTestPanel from './ImpairmentTestPanel';
import { FormRow, InlineError, inputStyle, btnPrimary, btnSecondary } from './CostLedgerPanel';

const API = `${API_V1}/revenue`;

interface CostAsset {
  id: number;
  revenue_contract_id: number;
  pob_id: number | null;
  cost_type: string;
  amount: number;
  currency: string;
  description: string;
  status: string;
  carrying_amount: number;
  amortization_method: string;
  expected_useful_life_months: number | null;
  amortization_start_date: string | null;
  recoverable_affirmed: boolean;
  reviewer_rationale: string;
  capitalization_je_id: number | null;
  capitalization_je_number: string | null;
  capitalized_at: string | null;
  capitalized_by: number | null;
  notes: string | null;
}

interface AmortizationEvent {
  event_id: number;
  cost_asset_id: number;
  period: string;
  status: string;
  amortization_method: string;
  pct_applied: number | null;
  carrying_amount_before: number;
  carrying_amount_after: number;
  amount_amortized: number;
  je_id: number | null;
  je_number: string | null;
  trigger_source: string;
  source_note: string | null;
  created_at: string | null;
}

interface Props {
  contractId: number;
  contractStatus: string;
  pobs?: Array<{ id: number; po_number: number; description: string }>;
  onStateChanged?: () => void;
}

export default function ContractCostPanel({ contractId, contractStatus, pobs, onStateChanged }: Props) {
  const [tab, setTab] = useState<'assets' | 'amortization' | 'impairment'>('assets');
  const [assets, setAssets] = useState<CostAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${API}/contracts/${contractId}/acquisition-costs?limit=500`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setAssets(data.assets || []);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => { load(); }, [load]);

  const statusBadge = (s: string) => {
    let bg = 'rgba(100,116,139,0.15)';
    let color = 'var(--text-secondary)';
    if (s === 'ACTIVE') { bg = 'rgba(34,197,94,0.15)'; color = 'var(--positive)'; }
    else if (s === 'FULLY_AMORTIZED') { bg = 'rgba(100,116,139,0.15)'; color = 'var(--text-secondary)'; }
    else if (s === 'IMPAIRED') { bg = 'rgba(234,179,8,0.15)'; color = 'var(--warning)'; }
    else if (s === 'EXPENSED_25_4') { bg = 'rgba(59,130,246,0.1)'; color = 'var(--accent)'; }
    else if (s === 'REVERSED') { bg = 'rgba(239,68,68,0.15)'; color = 'var(--negative)'; }
    return <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: bg, color }}>{s}</span>;
  };

  const assetCols: Column[] = [
    { key: 'cost_type', label: 'Type', render: (v: string) => <span style={{ fontSize: 11 }}>{v}</span> },
    { key: 'description', label: 'Description' },
    { key: 'status', label: 'Status', render: (v: string) => statusBadge(v) },
    { key: 'amount', label: 'Amount', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'carrying_amount', label: 'Carrying', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'amortization_method', label: 'Method' },
    { key: 'capitalized_at', label: 'Capitalized', render: (v: string | null) => v ? new Date(v).toLocaleDateString() : '-' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <TabBar
        tabs={[
          { key: 'assets', label: 'Capitalized Assets', count: assets.length },
          { key: 'amortization', label: 'Amortization Events' },
          { key: 'impairment', label: 'Impairment Testing' },
        ]}
        active={tab}
        onChange={(k) => setTab(k as any)}
      />

      {tab === 'assets' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => setCreateOpen(true)} style={btnPrimary}>
              Capitalize Cost
            </button>
          </div>

          {error && (
            <div style={{ padding: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid var(--negative)', borderRadius: 4, fontSize: 12, color: 'var(--negative)' }}>
              {error}
            </div>
          )}
          {flash && (
            <div style={{ padding: 10, background: 'rgba(34,197,94,0.1)', border: '1px solid var(--positive)', borderRadius: 4, fontSize: 12, color: 'var(--positive)' }}>
              {flash}
            </div>
          )}

          <DataTable columns={assetCols} rows={assets} loading={loading}
            emptyMessage="No capitalized cost assets on this contract yet."
            rowKey="id" />

          <CostAssetFormModal
            open={createOpen}
            onClose={() => setCreateOpen(false)}
            contractId={contractId}
            pobs={pobs || []}
            onSaved={async (msg) => {
              setCreateOpen(false);
              setFlash(msg);
              await load();
              if (onStateChanged) onStateChanged();
            }}
          />
        </div>
      )}

      {tab === 'amortization' && (
        <AmortizationEventsPanel contractId={contractId} assets={assets} onStateChanged={async () => { await load(); if (onStateChanged) onStateChanged(); }} />
      )}

      {tab === 'impairment' && (
        <ImpairmentTestPanel contractId={contractId} onStateChanged={async () => { await load(); if (onStateChanged) onStateChanged(); }} />
      )}
    </div>
  );
}

// ─── Capitalize Cost Asset Modal ──────────────────────────────

function CostAssetFormModal({ open, onClose, contractId, pobs, onSaved }: {
  open: boolean; onClose: () => void; contractId: number;
  pobs: Array<{ id: number; po_number: number; description: string }>;
  onSaved: (msg: string) => void;
}) {
  const [costType, setCostType] = useState('ACQUISITION');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [recoverable, setRecoverable] = useState(false);
  const [method, setMethod] = useState('STRAIGHT_LINE');
  const [rationale, setRationale] = useState('');
  const [usefulLife, setUsefulLife] = useState('');
  const [pobId, setPobId] = useState<string>('');
  const [acceptExpedient, setAcceptExpedient] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [expedientSuggestion, setExpedientSuggestion] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setAmount(''); setDescription(''); setRecoverable(false);
    setMethod('STRAIGHT_LINE'); setRationale(''); setUsefulLife('');
    setPobId(''); setAcceptExpedient(false); setErr(null);
    setExpedientSuggestion(null);
  };

  const submit = async () => {
    setErr(null); setExpedientSuggestion(null);
    const amt = parseFloat(amount);
    if (!(amt > 0)) { setErr('Amount must be > 0.'); return; }
    if (!description.trim()) { setErr('Description is required.'); return; }
    if (!recoverable) { setErr('Recoverable attestation is required (340-40-25-3).'); return; }
    if (rationale.trim().length < 30) { setErr('Rationale must be at least 30 chars.'); return; }
    if (method === 'POB_PROGRESS' && !pobId) { setErr('POB_PROGRESS requires selecting a POB.'); return; }
    if ((method === 'STRAIGHT_LINE' || method === 'POINT_IN_TIME') && !usefulLife) {
      setErr(`${method} requires expected_useful_life_months.`); return;
    }
    setBusy(true);
    try {
      const body: any = {
        cost_type: costType,
        amount: amt,
        description: description.trim(),
        recoverable_affirmed: recoverable,
        amortization_method: method,
        reviewer_rationale: rationale.trim(),
        accept_expedient: acceptExpedient,
      };
      if (usefulLife) body.expected_useful_life_months = parseInt(usefulLife, 10);
      if (pobId) body.pob_id = parseInt(pobId, 10);
      const res = await authFetch(`${API}/contracts/${contractId}/acquisition-costs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        onSaved(`Cost asset ${data.id} capitalized (status=${data.status}).`);
        reset();
      } else if (res.status === 409) {
        const body2 = await res.json().catch(() => ({ detail: {} }));
        const d = body2.detail || {};
        if (d.code === 'EXPEDIENT_SUGGESTED') {
          // Show inline expedient prompt — reviewer can accept (capitalize anyway) or switch method
          setExpedientSuggestion(d);
        } else {
          setErr(d.message || d.code || JSON.stringify(d));
        }
      } else {
        const body2 = await res.json().catch(() => ({ detail: 'Unknown error' }));
        const detail = typeof body2.detail === 'string' ? body2.detail : body2.detail?.message || JSON.stringify(body2.detail);
        setErr(detail || `HTTP ${res.status}`);
      }
    } catch (e: any) {
      setErr(e?.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={() => { reset(); onClose(); }}
      title="Capitalize Contract Cost (ASC 340-40-25-1/25-5)"
      description="Post DR Capitalized Asset / CR Operating Expense on submit. 340-40-25-4 expedient triggers inline prompt when period ≤ tenant threshold."
      size="lg"
      footer={
        <>
          <button type="button" onClick={() => { reset(); onClose(); }} style={btnSecondary} disabled={busy}>Cancel</button>
          <button type="button" onClick={submit} style={btnPrimary} disabled={busy}>
            {busy ? 'Saving...' : 'Capitalize'}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <FormRow label="Cost Type">
          <select value={costType} onChange={(e) => setCostType(e.target.value)} style={inputStyle}>
            <option value="ACQUISITION">ACQUISITION (25-1: commission, bonus, spiff)</option>
            <option value="FULFILLMENT">FULFILLMENT (25-5: setup, tooling, mobilization)</option>
          </select>
        </FormRow>
        <FormRow label="Amount ($)">
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} style={inputStyle} step="0.01" />
        </FormRow>
        <div style={{ gridColumn: '1 / 3' }}>
          <FormRow label="Description">
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} style={inputStyle} />
          </FormRow>
        </div>
        <FormRow label="Amortization Method">
          <select value={method} onChange={(e) => setMethod(e.target.value)} style={inputStyle}>
            <option value="STRAIGHT_LINE">STRAIGHT_LINE (over useful life)</option>
            <option value="POB_PROGRESS">POB_PROGRESS (tracks POB pct)</option>
            <option value="POINT_IN_TIME">POINT_IN_TIME (full at POB complete)</option>
            <option value="EXPENSE_IMMEDIATE">EXPENSE_IMMEDIATE (25-4 expedient)</option>
          </select>
        </FormRow>
        {(method === 'STRAIGHT_LINE' || method === 'POINT_IN_TIME') && (
          <FormRow label="Expected Useful Life (months)">
            <input type="number" value={usefulLife} onChange={(e) => setUsefulLife(e.target.value)} style={inputStyle} min="1" />
          </FormRow>
        )}
        {method === 'POB_PROGRESS' && (
          <FormRow label="POB">
            <select value={pobId} onChange={(e) => setPobId(e.target.value)} style={inputStyle}>
              <option value="">— select —</option>
              {pobs.map((p) => (
                <option key={p.id} value={p.id}>#{p.po_number} · {p.description?.slice(0, 50)}</option>
              ))}
            </select>
          </FormRow>
        )}
        <div style={{ gridColumn: '1 / 3' }}>
          <FormRow label="340-40-25-3 Recoverable Attestation">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={recoverable} onChange={(e) => setRecoverable(e.target.checked)} />
              <span style={{ fontSize: 12 }}>I attest the entity reasonably expects to recover these costs.</span>
            </label>
          </FormRow>
        </div>
        <div style={{ gridColumn: '1 / 3' }}>
          <FormRow label="Reviewer Rationale (≥30 chars)">
            <textarea value={rationale} onChange={(e) => setRationale(e.target.value)}
              style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
              placeholder="Capitalization basis: which 340-40 criterion applies + evidence..." />
            <div style={{ fontSize: 10, color: rationale.length >= 30 ? 'var(--positive)' : 'var(--text-hint)', marginTop: 2 }}>
              {rationale.length}/30
            </div>
          </FormRow>
        </div>
      </div>
      {expedientSuggestion && (
        <div style={{ marginTop: 10, padding: 10, background: 'rgba(234,179,8,0.1)', border: '1px solid var(--warning)', borderRadius: 4, fontSize: 12 }}>
          <div style={{ fontWeight: 700, color: 'var(--warning)', marginBottom: 4 }}>
            ASC 340-40-25-4 practical expedient available
          </div>
          <div style={{ color: 'var(--text-primary)' }}>
            Effective life ({expedientSuggestion.effective_life_months} months) ≤ tenant threshold ({expedientSuggestion.threshold_months} months).
            You may expense immediately instead of capitalizing.
          </div>
          <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => { setMethod('EXPENSE_IMMEDIATE'); setExpedientSuggestion(null); }}
              style={{ ...btnSecondary, background: 'var(--warning)', color: 'white' }}>
              Accept Expedient (expense immediately)
            </button>
            <button type="button" onClick={() => { setAcceptExpedient(true); setExpedientSuggestion(null); }}
              style={btnSecondary}>
              Override — capitalize anyway (re-submit)
            </button>
          </div>
        </div>
      )}
      {err && <InlineError>{err}</InlineError>}
    </Modal>
  );
}

// ─── Amortization Events Sub-Panel ────────────────────────────

function AmortizationEventsPanel({ contractId, assets, onStateChanged }: {
  contractId: number;
  assets: CostAsset[];
  onStateChanged: () => void;
}) {
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(assets[0]?.id || null);
  const [events, setEvents] = useState<AmortizationEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [runBusy, setRunBusy] = useState(false);

  useEffect(() => {
    if (assets.length > 0 && selectedAssetId === null) {
      setSelectedAssetId(assets[0].id);
    }
  }, [assets, selectedAssetId]);

  const load = useCallback(async () => {
    if (!selectedAssetId) return;
    setLoading(true); setError(null);
    try {
      const res = await authFetch(
        `${API}/contracts/${contractId}/cost-assets/${selectedAssetId}/amortization-events?limit=200`,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setEvents(data.events || []);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [contractId, selectedAssetId]);

  useEffect(() => { load(); }, [load]);

  const runAmortization = async () => {
    if (!selectedAssetId) return;
    setError(null); setFlash(null); setRunBusy(true);
    try {
      const res = await authFetch(`${API}/contracts/${contractId}/cost-assets/run-amortization`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ asset_id: selectedAssetId }),
      });
      if (res.ok) {
        const data = await res.json();
        setFlash(`Amortization fired: status=${data.status}, Δ ${data.amount_amortized ? formatAmount(data.amount_amortized) : '$0'}`);
        await load();
        if (onStateChanged) onStateChanged();
      } else {
        const body = await res.json().catch(() => ({ detail: 'Unknown error' }));
        const detail = typeof body.detail === 'string' ? body.detail : body.detail?.message || JSON.stringify(body.detail);
        setError(detail || `HTTP ${res.status}`);
      }
    } catch (e: any) {
      setError(e?.message || 'Network error');
    } finally {
      setRunBusy(false);
    }
  };

  const statusBadge = (s: string) => {
    let bg = 'rgba(100,116,139,0.15)';
    let color = 'var(--text-secondary)';
    if (s === 'AMORTIZED' || s === 'FINAL_AMORTIZATION') { bg = 'rgba(34,197,94,0.15)'; color = 'var(--positive)'; }
    else if (s === 'FAILED') { bg = 'rgba(239,68,68,0.15)'; color = 'var(--negative)'; }
    return <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: bg, color }}>{s}</span>;
  };

  const cols: Column[] = [
    { key: 'period', label: 'Period', width: 90 },
    { key: 'status', label: 'Status', render: (v: string) => statusBadge(v) },
    { key: 'amortization_method', label: 'Method', render: (v: string) => <span style={{ fontSize: 11 }}>{v}</span> },
    { key: 'pct_applied', label: 'Pct', align: 'right', render: (v: number | null) => v == null ? '-' : `${(v * 100).toFixed(2)}%` },
    { key: 'amount_amortized', label: 'Δ Amortized', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'carrying_amount_after', label: 'Carrying After', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'je_number', label: 'JE #', render: (v: string | null) => v || '-' },
    { key: 'trigger_source', label: 'Trigger', render: (v: string) => <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{v}</span> },
    { key: 'created_at', label: 'Posted', render: (v: string | null) => v ? new Date(v).toLocaleString() : '-' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {assets.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-hint)', background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)' }}>
          No cost assets yet — capitalize one via the Assets tab.
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 300 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Cost Asset</label>
              <select
                value={selectedAssetId || ''}
                onChange={(e) => setSelectedAssetId(parseInt(e.target.value, 10) || null)}
                style={{ ...inputStyle, maxWidth: 500 }}
              >
                {assets.map((a) => (
                  <option key={a.id} value={a.id}>
                    #{a.id} · {a.description.slice(0, 60)} · ${a.carrying_amount.toLocaleString()} / ${a.amount.toLocaleString()}
                  </option>
                ))}
              </select>
            </div>
            <button type="button" onClick={runAmortization} disabled={runBusy || !selectedAssetId} style={btnPrimary}>
              {runBusy ? 'Running...' : 'Run Amortization'}
            </button>
          </div>

          {error && (
            <div style={{ padding: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid var(--negative)', borderRadius: 4, fontSize: 12, color: 'var(--negative)' }}>
              {error}
            </div>
          )}
          {flash && (
            <div style={{ padding: 10, background: 'rgba(34,197,94,0.1)', border: '1px solid var(--positive)', borderRadius: 4, fontSize: 12, color: 'var(--positive)' }}>
              {flash}
            </div>
          )}

          <DataTable columns={cols} rows={events} loading={loading}
            emptyMessage="No amortization events for this asset yet."
            rowKey="event_id" />
        </>
      )}
    </div>
  );
}
